/**
 * form.js — Formulário de Contato / Agendamento ("Fale Conosco")
 *
 * - Campos: Nome, Telefone (com máscara), Interesse (select), Mensagem (opcional)
 * - veiculo_id: null (contato genérico da homepage)
 * - Integração com registrarLeadPublico() — origem: 'Contato'
 * - Validação inline + live-clear de erros
 * - Sistema de tabs: "Enviar mensagem" | "Como chegar"
 * - Estado de sucesso com feedback visual
 */

import { registrarLeadPublico } from '../services/leads.js';

// ── Helpers ────────────────────────────────────────────────────
const $ = id => document.getElementById(id);

// ── Máscara de telefone ────────────────────────────────────────
function mascaraTelefone(input) {
  input.addEventListener('input', () => {
    let v = input.value.replace(/\D/g, '').slice(0, 11);
    if (v.length > 6)      v = `(${v.slice(0, 2)}) ${v.slice(2, 7)}-${v.slice(7)}`;
    else if (v.length > 2) v = `(${v.slice(0, 2)}) ${v.slice(2)}`;
    else if (v.length > 0) v = `(${v}`;
    input.value = v;
  });
}

// ── Validação ──────────────────────────────────────────────────
function setError(inputId, errorId, msg) {
  const el  = $(inputId);
  const err = $(errorId);
  if (!el || !err) return;
  if (msg) {
    el.classList.add('contact-invalid');
    err.textContent = msg;
  } else {
    el.classList.remove('contact-invalid');
    err.textContent = '';
  }
}

function clearAllErrors() {
  ['contact-nome', 'contact-telefone'].forEach(id => {
    $(id)?.classList.remove('contact-invalid');
  });
  ['cerr-nome', 'cerr-telefone'].forEach(id => {
    const el = $(id); if (el) el.textContent = '';
  });
  $('cerr-global')?.classList.remove('show');
}

function validate() {
  clearAllErrors();
  let valid = true;

  const nome     = $('contact-nome')?.value.trim()     ?? '';
  const telefone = $('contact-telefone')?.value.trim() ?? '';

  if (!nome) {
    setError('contact-nome', 'cerr-nome', 'Nome é obrigatório.');
    valid = false;
  }

  const rawPhone = telefone.replace(/\D/g, '');
  if (!rawPhone || rawPhone.length < 10) {
    setError('contact-telefone', 'cerr-telefone', 'Informe um WhatsApp válido.');
    valid = false;
  }

  return valid;
}

// ── Estado de sucesso / reset ──────────────────────────────────
function mostrarSucesso() {
  $('contact-form-state')?.setAttribute('hidden', '');
  $('contact-sucesso')?.classList.remove('contact-sucesso--hidden');
}

function resetarFormulario() {
  $('form-contato')?.reset();
  clearAllErrors();
  const btn = $('btn-contact-submit');
  if (btn) { btn.disabled = false; btn.textContent = 'Enviar mensagem'; }
  $('contact-form-state')?.removeAttribute('hidden');
  $('contact-sucesso')?.classList.add('contact-sucesso--hidden');
}

// ── Submit ─────────────────────────────────────────────────────
async function handleSubmit(e) {
  e.preventDefault();
  if (!validate()) return;

  const btn      = $('btn-contact-submit');
  const nome     = $('contact-nome').value.trim();
  const tel      = $('contact-telefone').value.replace(/\D/g, '');
  const interesse = $('contact-interesse')?.value || 'Visita ao showroom';
  const msg      = $('contact-mensagem')?.value.trim() || null;

  btn.disabled    = true;
  btn.textContent = 'Enviando...';

  // Ocultar erro global prévio
  $('cerr-global')?.classList.remove('show');

  const mensagem = [
    `Interesse: ${interesse}`,
    msg ? `Mensagem: ${msg}` : null,
  ].filter(Boolean).join('\n');

  const payload = {
    nome,
    telefone:   tel,
    veiculo_id: null,
    mensagem,
    origem:     'Contato',
  };

  const { data, error } = await registrarLeadPublico(payload);

  if (error || !data?.ok) {
    console.error('[form-contato] Erro ao enviar lead:', error || data);
    btn.disabled    = false;
    btn.textContent = 'Enviar mensagem';
    
    // Feedback visual elegante em vez do alert
    const globalError = $('cerr-global');
    const globalErrorText = $('cerr-global-text');
    if (globalError && globalErrorText) {
      globalErrorText.textContent = error?.message || data?.error || 'Ocorreu um erro ao enviar. Por favor, tente novamente ou fale pelo WhatsApp.';
      globalError.classList.add('show');
    }
    return;
  }

  mostrarSucesso();
}

// ── Tabs (Formulário / Mapa) ───────────────────────────────────
function initTabs() {
  const tabs    = document.querySelectorAll('.contact-tab-btn');
  const panels  = document.querySelectorAll('.contact-tab-panel');

  tabs.forEach(tab => {
    tab.addEventListener('click', () => {
      const target = tab.dataset.tab;

      tabs.forEach(t => {
        t.classList.toggle('active', t.dataset.tab === target);
        t.setAttribute('aria-selected', t.dataset.tab === target);
      });

      panels.forEach(p => {
        p.hidden = p.dataset.panel !== target;
      });
    });
  });
}

// ── Inicialização ──────────────────────────────────────────────
export function initForm() {
  const form = $('form-contato');
  if (!form) return;

  mascaraTelefone($('contact-telefone'));

  // Live-clear de erros
  ['contact-nome', 'contact-telefone'].forEach(id => {
    $(id)?.addEventListener('input', () => {
      $(id)?.classList.remove('contact-invalid');
      const errId = id.replace('contact-', 'cerr-');
      const errEl = $(errId);
      if (errEl) errEl.textContent = '';
    });
  });

  form.addEventListener('submit', handleSubmit);
  $('btn-contact-nova')?.addEventListener('click', resetarFormulario);

  initTabs();
}
